import humps from "humps"
import { DirectUpload } from "@rails/activestorage"
import { getMetaVar } from "./getMetaVar"

export interface UploadProgressEvent {
  id: string
  file: File
  progress: number
}

export interface UploadErrorEvent {
  id: string
  file: File
  error: Error
}

export interface UploadCompleteEvent {
  id: string
  file: File
  blob: any
}

export interface DirectUploadOptions {
  onStart?: (id: string, file: File) => void
  onProgress?: (event: UploadProgressEvent) => void
  onError?: (event: UploadErrorEvent) => void
  onComplete?: (event: UploadCompleteEvent) => void
}

export const directFileUpload = async (
  file: File | FileList[number],
  options: DirectUploadOptions = {}
): Promise<any> => {
  const data = await uploadFile(file, options)
  return humps.camelizeKeys(data)
}

const directUploadsUrl = getMetaVar("direct-uploads-url")

class DirectUploadController {
  file: File
  url: string
  upload: any
  id: string
  options: DirectUploadOptions

  constructor(file: File, url: string, options: DirectUploadOptions = {}) {
    this.file = file
    this.url = url
    this.options = options
    this.id = Math.random().toString(36).substring(2, 15)
    this.upload = new DirectUpload(file, url, this)
  }

  start(): Promise<any> {
    if (this.options.onStart) {
      this.options.onStart(this.id, this.file)
    }

    return new Promise((resolve, reject) => {
      this.upload.create((error: Error, blob: any) => {
        if (error) {
          if (this.options.onError) {
            this.options.onError({
              id: this.id,
              file: this.file,
              error,
            })
          }
          reject(error)
        } else {
          if (this.options.onComplete) {
            this.options.onComplete({
              id: this.id,
              file: this.file,
              blob,
            })
          }
          resolve(blob)
        }
      })
    })
  }

  directUploadWillStoreFileWithXHR(request: XMLHttpRequest) {
    request.upload.addEventListener("progress", (event) =>
      this.directUploadDidProgress(event)
    )
  }

  directUploadDidProgress(event: ProgressEvent) {
    if (this.options.onProgress) {
      const progress = Math.round((event.loaded / event.total) * 100)
      this.options.onProgress({
        id: this.id,
        file: this.file,
        progress,
      })
    }
  }
}

const uploadFile = (
  file: File,
  options: DirectUploadOptions = {}
): Promise<any> => {
  const controller = new DirectUploadController(file, directUploadsUrl, options)
  return controller.start()
}

export const setupDropZone = (
  element: HTMLElement,
  options: DirectUploadOptions = {}
) => {
  element.addEventListener("dragover", (event) => {
    event.preventDefault()
    element.classList.add("dragover")
  })

  element.addEventListener("dragleave", () => {
    element.classList.remove("dragover")
  })

  element.addEventListener("drop", (event) => {
    event.preventDefault()
    element.classList.remove("dragover")

    if (event.dataTransfer?.files) {
      Array.from(event.dataTransfer.files).forEach((file) => {
        uploadFile(file, options)
      })
    }
  })

  return () => {
    element.removeEventListener("dragover", () => {})
    element.removeEventListener("dragleave", () => {})
    element.removeEventListener("drop", () => {})
  }
}
